import React, { useState, useMemo, useEffect } from "react";
import {
  Search,
  ArrowRight,
  RefreshCw,
  TrendingUp,
  TrendingDown,
  Activity,
  MapPin,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
  MessageSquare,
  X,
  ClipboardList,
  Mail,
  CheckCircle,
  Info,
} from "lucide-react";
import PatientChoicePanel from "./PatientChoicePanel";

const metricDefinitions = [
  { label: "Mobility at Discharge", currentKey: null, historicalKey: "mobility_at_discharge", higherIsBetter: true, unit: "%", description: "Higher indicates patients regained more function", preference: "Higher is better", group: "outcomes" },
  { label: "Discharge to Community", currentKey: null, historicalKey: "discharge_to_community_rate", higherIsBetter: true, unit: "%", description: "Share of residents successfully discharged home", preference: "Higher is better", group: "outcomes" },
  { label: "Self-Care at Discharge", currentKey: null, historicalKey: "self_care_at_discharge", higherIsBetter: true, unit: "%", description: "Improvement in residents' ability to perform daily tasks", preference: "Higher is better", group: "outcomes" },
  { label: "Preventable Readmission", currentKey: null, historicalKey: "preventable_readmission_rate", higherIsBetter: false, unit: "%", description: "Lower suggests fewer residents bounce back to hospitals", preference: "Lower is better", group: "safety" },
  { label: "Fall w/ Major Injury", currentKey: null, historicalKey: "fall_with_major_injury_rate", higherIsBetter: false, unit: "%", description: "Lower is safer; serious fall incidents", preference: "Lower is better", group: "safety" },
  { label: "Pressure Ulcer Rate", currentKey: null, historicalKey: "pressure_ulcer_rate", higherIsBetter: false, unit: "%", description: "Lower indicates better wound prevention", preference: "Lower is better", group: "safety" },
  { label: "Infection Rate", currentKey: null, historicalKey: "healthcare_associated_infection_rate", higherIsBetter: false, unit: "%", description: "Lower is better; infection control indicator", preference: "Lower is better", group: "safety" },
  { label: "Medication Review", currentKey: null, historicalKey: "medication_review_rate", higherIsBetter: true, unit: "%", description: "Residents receiving appropriate med reviews", preference: "Higher is better", group: "process" },
  { label: "Short-stay Rehospitalization", currentKey: null, historicalKey: "short_stay_rehospitalization_rate", higherIsBetter: false, unit: "%", description: "After SNF admission, lower rehospitalization is better", preference: "Lower is better", group: "utilization" },
  { label: "Short-stay ED Visit", currentKey: null, historicalKey: "short_stay_ed_visit_rate", higherIsBetter: false, unit: "%", description: "ED visits after SNF admission (short-stay)", preference: "Lower is better", group: "utilization" },
  { label: "Long-stay Hospitalizations", currentKey: null, historicalKey: "long_stay_hospitalization_rate", higherIsBetter: false, unit: "", description: "Per 1000 long-stay resident days", preference: "Lower is better", group: "utilization" },
  { label: "Long-stay ED Visits", currentKey: null, historicalKey: "long_stay_ed_visit_rate", higherIsBetter: false, unit: "", description: "Per 1000 long-stay resident days", preference: "Lower is better", group: "utilization" },
  { label: "Long-stay Antipsychotic Use", currentKey: null, historicalKey: "long_stay_antipsychotic_rate", higherIsBetter: false, unit: "%", description: "Lower indicates less use of antipsychotics", preference: "Lower is better", group: "longStay" },
  { label: "Long-stay Falls w/ Major Injury", currentKey: null, historicalKey: "long_stay_fall_with_major_injury_rate", higherIsBetter: false, unit: "%", description: "Lower is safer for long-stay residents", preference: "Lower is better", group: "longStay" },
  { label: "Long-stay Pressure Ulcers", currentKey: null, historicalKey: "long_stay_pressure_ulcer_rate", higherIsBetter: false, unit: "%", description: "Lower indicates better wound prevention", preference: "Lower is better", group: "longStay" },
  { label: "Long-stay UTI", currentKey: null, historicalKey: "long_stay_uti_rate", higherIsBetter: false, unit: "%", description: "Lower indicates fewer urinary tract infections", preference: "Lower is better", group: "longStay" },
  { label: "Long-stay Catheter Use", currentKey: null, historicalKey: "long_stay_catheter_rate", higherIsBetter: false, unit: "%", description: "Lower indicates less catheter use", preference: "Lower is better", group: "longStay" },
  { label: "Long-stay Mobility Decline", currentKey: null, historicalKey: "long_stay_mobility_decline_rate", higherIsBetter: false, unit: "%", description: "Lower indicates less mobility decline", preference: "Lower is better", group: "longStay" },
  { label: "Long-stay ADL Decline", currentKey: null, historicalKey: "long_stay_adl_decline_rate", higherIsBetter: false, unit: "%", description: "Lower indicates less help needed with daily activities", preference: "Lower is better", group: "longStay" },
  { label: "Long-stay Weight Loss", currentKey: null, historicalKey: "long_stay_weight_loss_rate", higherIsBetter: false, unit: "%", description: "Lower indicates fewer residents losing too much weight", preference: "Lower is better", group: "longStay" },
  { label: "Long-stay Depression", currentKey: null, historicalKey: "long_stay_depression_rate", higherIsBetter: false, unit: "%", description: "Lower indicates fewer depressive symptoms", preference: "Lower is better", group: "longStay" },
  { label: "Long-stay Incontinence", currentKey: null, historicalKey: "long_stay_incontinence_rate", higherIsBetter: false, unit: "%", description: "Lower indicates fewer new or worsened cases", preference: "Lower is better", group: "longStay" },
  { label: "Long-stay Restraints", currentKey: null, historicalKey: "long_stay_restraint_rate", higherIsBetter: false, unit: "%", description: "Lower indicates fewer residents restrained", preference: "Lower is better", group: "longStay" },
  { label: "Short-stay Flu Vaccine", currentKey: null, historicalKey: "short_stay_flu_vaccine_rate", higherIsBetter: true, unit: "%", description: "Higher indicates flu vaccine coverage", preference: "Higher is better", group: "prevention" },
  { label: "Long-stay Flu Vaccine", currentKey: null, historicalKey: "long_stay_flu_vaccine_rate", higherIsBetter: true, unit: "%", description: "Higher indicates flu vaccine coverage", preference: "Higher is better", group: "prevention" },
  { label: "Short-stay Pneumococcal Vaccine", currentKey: null, historicalKey: "short_stay_pneumococcal_vaccine_rate", higherIsBetter: true, unit: "%", description: "Higher indicates pneumococcal vaccine coverage", preference: "Higher is better", group: "prevention" },
  { label: "Long-stay Pneumococcal Vaccine", currentKey: null, historicalKey: "long_stay_pneumococcal_vaccine_rate", higherIsBetter: true, unit: "%", description: "Higher indicates pneumococcal vaccine coverage", preference: "Higher is better", group: "prevention" },
  { label: "Med List to Next Provider", currentKey: null, historicalKey: "med_list_to_next_provider_rate", higherIsBetter: true, unit: "%", description: "Medication list sent to next care setting", preference: "Higher is better", group: "careTransitions" },
  { label: "Med List at Transition", currentKey: null, historicalKey: "med_list_at_transition_rate", higherIsBetter: true, unit: "%", description: "Medication list provided at discharge", preference: "Higher is better", group: "careTransitions" },
  { label: "Staff Flu Vaccination", currentKey: null, historicalKey: "staff_flu_vaccination_rate", higherIsBetter: true, unit: "%", description: "Staff flu vaccination coverage", preference: "Higher is better", group: "staffing" },
  { label: "Staff COVID Vaccination", currentKey: null, historicalKey: "staff_covid_vaccination_rate", higherIsBetter: true, unit: "%", description: "Staff COVID vaccination coverage", preference: "Higher is better", group: "staffing" },
  { label: "MSPB Score", currentKey: null, historicalKey: "spend_per_beneficiary_score", higherIsBetter: false, unit: "", description: "Lower indicates more efficient post-acute spending", preference: "Lower is better", group: "value" },
];

const metricGroupLabels = {
  outcomes: "Patient Outcomes",
  safety: "Safety & Risk",
  process: "Care Process",
  utilization: "Utilization & Access",
  longStay: "Long-Stay Well-Being",
  prevention: "Prevention & Vaccinations",
  careTransitions: "Care Transitions",
  staffing: "Staff Readiness",
  value: "Cost & Value",
};

const PRIMARY_GROUPS = new Set(["outcomes", "safety", "process", "utilization"]);

const API_BASE =
  (process.env.REACT_APP_API_URL && process.env.REACT_APP_API_URL.replace(/\/$/, "")) ||
  "";

// Sales-facing data footprint indicator (based on current loaded dataset snapshot).
const DATA_FOOTPRINT = {
  qrpRows: 3039236,
  snfs: 14721,
  hospitals: 5416,
};

const formatCompactNumber = (value) => {
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M+`;
  if (value >= 1_000) return `${(value / 1_000).toFixed(1)}K+`;
  return String(value);
};

const formatNumber = (value, fractionDigits = 1) => {
  if (value === null || value === undefined || Number.isNaN(value)) return "—";
  return Number(value).toFixed(fractionDigits);
};

const formatCurrency = (value) => {
  if (value === null || value === undefined || Number.isNaN(value)) return "—";
  return Number(value).toLocaleString(undefined, { style: "currency", currency: "USD", maximumFractionDigits: 0 });
};

const compositeBadge = (score) => {
  if (score == null) return { label: "No score", bg: "pill-neutral" };
  if (score >= 80) return { label: "Excellent", bg: "pill-emerald" };
  if (score >= 60) return { label: "Strong", bg: "pill-lime" };
  if (score >= 40) return { label: "Average", bg: "pill-amber" };
  if (score >= 20) return { label: "Below Avg", bg: "pill-orange" };
  return { label: "High Risk", bg: "pill-rose" };
};

const resolveMetricValue = (facility, metric) => {
  const currentValue = metric.currentKey && metric.currentKey in facility ? facility[metric.currentKey] : undefined;
  const history = facility.historical_metrics || {};
  const years = Object.keys(history)
    .map((y) => Number(y))
    .sort((a, b) => a - b);
  const latestSnapshotYear = years.length ? years[years.length - 1] : null;

  let historicalLatest = null;
  let historicalLatestYear = null;
  for (let i = years.length - 1; i >= 0; i -= 1) {
    const year = years[i];
    const value = history[year]?.[metric.historicalKey];
    if (value != null) {
      historicalLatest = value;
      historicalLatestYear = year;
      break;
    }
  }

  const value = currentValue ?? historicalLatest;
  let source = currentValue != null ? "current" : historicalLatest != null ? "historical" : null;
  if (source === "historical" && historicalLatestYear != null && historicalLatestYear === latestSnapshotYear) {
    source = "snapshot";
  }

  let trend = null;
  if (years.length >= 2 && historicalLatest != null) {
    let earliestValue = null;
    let earliestYear = null;
    for (let i = 0; i < years.length; i += 1) {
      const year = years[i];
      const value = history[year]?.[metric.historicalKey];
      if (value != null) {
        earliestValue = value;
        earliestYear = year;
        break;
      }
    }

    if (earliestValue != null && earliestYear != null && historicalLatestYear != null) {
      const delta = historicalLatest - earliestValue;
      const positive = delta > 0;
      const good = metric.higherIsBetter ? positive : !positive;
      trend = {
        delta,
        good,
        yearSpan: `${earliestYear}→${historicalLatestYear}`,
        startValue: earliestValue,
        endValue: historicalLatest,
      };
    }
  }

  const mostRecentYear = historicalLatestYear;
  return { value, source, historicalLatestYear, trend, mostRecentYear };
};

const formatMessageHtml = (text = "") => {
  const escape = (str) =>
    str
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");

  const escaped = escape(text);
  const withBold = escaped.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
  const withBreaks = withBold.replace(/\n/g, "<br />");
  return { __html: withBreaks };
};

const truncateReview = (text = "") => {
  if (!text) return "";
  return text.replace(/\s+/g, " ").trim();
};

function IntroModal({ onClose, onRequestDemo }) {
  return (
    <div className="demo-overlay" onClick={onClose}>
      <div className="intro-modal" onClick={(e) => e.stopPropagation()}>
        <button className="demo-modal-close" onClick={onClose} aria-label="Close">
          <X size={16} />
        </button>
        <p className="eyebrow">PAC Intelligence Platform</p>
        <h2>Explore Skilled Nursing Facility Performance</h2>
        <p className="demo-lede">
          Hospitals are increasingly accountable for what happens after discharge. Our platform
          aggregates publicly available CMS quality metrics and episode performance indicators
          to help hospital leaders understand the post-acute landscape surrounding their facilities.
        </p>
        <ul className="intro-bullets">
          <li>Which SNFs consistently perform better on quality and outcomes</li>
          <li>Which facilities may increase readmission risk</li>
          <li>How patient choice form rankings compare to quality-based rankings</li>
          <li>Regulatory history, staffing trends, and community feedback</li>
        </ul>
        <div className="intro-modal-actions">
          <button className="primary-btn" onClick={onClose}>
            Get Started <ArrowRight className="icon" />
          </button>
          <button className="ghost-btn" onClick={onRequestDemo}>
            Request a Demo
          </button>
        </div>
      </div>
    </div>
  );
}

function DemoModal({ onClose }) {
  const [form, setForm] = useState({ name: "", org: "", email: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [err, setErr] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setErr(null);
    try {
      const res = await fetch(`${API_BASE}/api/demo-request`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error("Request failed");
      setSubmitted(true);
    } catch {
      setErr("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="demo-overlay" onClick={onClose}>
      <div className="demo-modal" onClick={(e) => e.stopPropagation()}>
        <button className="demo-modal-close" onClick={onClose} aria-label="Close">
          <X size={16} />
        </button>
        {submitted ? (
          <div className="demo-success">
            <CheckCircle size={36} color="#1dd3b0" />
            <h2>We'll be in touch.</h2>
            <p>Thanks for your interest. Someone from our team will reach out shortly.</p>
            <button className="primary-btn" onClick={onClose}>Close</button>
          </div>
        ) : (
          <>
            <p className="eyebrow">PAC Intelligence Platform</p>
            <h2>Request a Personalized Demo</h2>
            <p className="demo-lede">
              See how your hospital can use post-acute performance data to reduce readmissions, improve SNF selection, and strengthen value-based care performance.
            </p>
            <form onSubmit={handleSubmit} className="demo-form">
              <div className="demo-field">
                <label>Name</label>
                <input
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  placeholder="Your name"
                />
              </div>
              <div className="demo-field">
                <label>Organization</label>
                <input
                  value={form.org}
                  onChange={(e) => setForm((f) => ({ ...f, org: e.target.value }))}
                  placeholder="Hospital or health system"
                />
              </div>
              <div className="demo-field">
                <label>Work Email *</label>
                <input
                  type="email"
                  required
                  value={form.email}
                  onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                  placeholder="you@hospital.org"
                />
              </div>
              {err && <div className="alert">{err}</div>}
              <button type="submit" className="primary-btn" disabled={submitting || !form.email}>
                {submitting ? "Submitting…" : <>Request Demo <ArrowRight className="icon" /></>}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

const SNFDashboard = () => {
  const [hospitalQuery, setHospitalQuery] = useState("");
  const [hospitalSuggestions, setHospitalSuggestions] = useState([]);
  const [selectedHospital, setSelectedHospital] = useState(null);
  const [searchError, setSearchError] = useState(null);
  const [facilities, setFacilities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastAnalyzedHospital, setLastAnalyzedHospital] = useState(null);
  const [chatHistory, setChatHistory] = useState([]);
  const [chatInput, setChatInput] = useState("");
  const [chatLoading, setChatLoading] = useState(false);
  const [chatError, setChatError] = useState(null);
  const [expandedFacilities, setExpandedFacilities] = useState({});
  const [showChatPanel, setShowChatPanel] = useState(false);
  const [showAllMeasures, setShowAllMeasures] = useState(false);
  const [showPatientChoicePanel, setShowPatientChoicePanel] = useState(false);
  const [dockCollapsed, setDockCollapsed] = useState(false);
  const [showDemoModal, setShowDemoModal] = useState(false);
  const [showIntroModal, setShowIntroModal] = useState(true);

  useEffect(() => {
    if (!hospitalQuery || hospitalQuery.length < 2) {
      setHospitalSuggestions([]);
      setSearchError(null);
      return;
    }

    const controller = new AbortController();
    const debounceId = setTimeout(() => {
      const fetchSuggestions = async () => {
        try {
          setSearchError(null);
          const params = new URLSearchParams({ q: hospitalQuery });
          const res = await fetch(`${API_BASE}/api/hospitals/search?${params.toString()}`, {
            signal: controller.signal,
          });
          if (!res.ok) {
            throw new Error(`Search failed (${res.status})`);
          }
          const data = await res.json();
          setHospitalSuggestions(data);
        } catch (err) {
          if (err.name !== "AbortError") console.error("Hospital search failed", err);
          if (err.name !== "AbortError") {
            setSearchError("Hospital search failed. Check the API connection.");
          }
        }
      };

      fetchSuggestions();
    }, 250);
    return () => {
      clearTimeout(debounceId);
      controller.abort();
    };
  }, [hospitalQuery]);

  const handleAnalyze = async () => {
    if (!selectedHospital) {
      alert("Please pick a hospital from the list before analyzing.");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_BASE}/api/analyze`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ hospitalName: selectedHospital.hospital_name }),
      });
      if (!response.ok) {
        const details = await response.json().catch(() => ({}));
        throw new Error(details.error || "Failed to fetch data");
      }
      const data = await response.json();
      setFacilities(data.facilities || []);
      setLastAnalyzedHospital(data.hospital);
      setExpandedFacilities({});
    } catch (err) {
      console.error(err);
      setError(err.message || "Unexpected error");
    } finally {
      setLoading(false);
    }
  };


  const summary = useMemo(() => {
    if (!facilities.length) return null;
    const avgDistance = facilities.reduce((sum, f) => sum + (f.distance || 0), 0) / facilities.length;
    const avgComposite = facilities.reduce((sum, f) => sum + (f.Composite_Score || 0), 0) / facilities.length;
    const historicalCoverage = facilities.reduce((sum, f) => sum + (f.historical_years_available || 0), 0) / facilities.length;
    return { avgDistance, avgComposite, historicalCoverage };
  }, [facilities]);

  const buildRegulatorySnapshot = (facility) => {
    const reg = facility.regulatory_history || {};
    const citationYears = Object.keys(reg.citations || {})
      .map(Number)
      .sort((a, b) => a - b);
    const penaltyYears = Object.keys(reg.penalties || {})
      .map(Number)
      .sort((a, b) => a - b);
    const latestCitationYear = citationYears[citationYears.length - 1];
    const latestPenaltyYear = penaltyYears[penaltyYears.length - 1];
    const citationData = latestCitationYear ? reg.citations[latestCitationYear] : null;
    return {
      citationYear: latestCitationYear,
      citationData,
      penaltyYear: latestPenaltyYear,
      penaltyData: latestPenaltyYear ? reg.penalties[latestPenaltyYear] : null,
      latestSurveyDate: citationData?.latestSurveyDate || null,
      topCategories: citationData?.topCategories || [],
      topTags: citationData?.topTags || [],
      recentCitations: citationData?.recent || [],
    };
  };

  const handleChatSubmit = async () => {
    if (!selectedHospital || !chatInput.trim()) return;
    const question = chatInput.trim();
    setChatInput("");
    setChatError(null);
    setChatHistory((prev) => [...prev, { role: "user", text: question }]);

    setChatLoading(true);
    try {
      const outgoingHistory = [...chatHistory, { role: "user", text: question }];
      const res = await fetch(`${API_BASE}/api/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          hospitalName: selectedHospital.hospital_name,
          question,
          history: outgoingHistory,
        }),
      });
      if (!res.ok) {
        const details = await res.json().catch(() => ({}));
        throw new Error(details.error || "Chat request failed");
      }
      const data = await res.json();
      setChatHistory((prev) => [...prev, { role: "assistant", text: data.reply }]);
    } catch (err) {
      console.error(err);
      setChatError(err.message || "Chat failed");
      setChatHistory((prev) => [...prev, { role: "assistant", text: "Sorry, I couldn't generate a response right now." }]);
    } finally {
      setChatLoading(false);
    }
  };


  return (
    <div className="dashboard-shell">
      {/* Tool dock — right-side vertical sidebar */}
      <nav className={`tool-dock${dockCollapsed ? " tool-dock-collapsed" : ""}`}>
        {/* Collapse handle — always peeks out from edge */}
        <button
          className="tool-dock-toggle"
          onClick={() => setDockCollapsed((v) => !v)}
          title={dockCollapsed ? "Show tools" : "Hide tools"}
        >
          {dockCollapsed ? <ChevronLeft size={11} /> : <ChevronRight size={11} />}
        </button>

        <button
          className={`tool-dock-btn ${showPatientChoicePanel ? "tool-dock-active" : ""}`}
          onClick={() => setShowPatientChoicePanel((v) => !v)}
          title="Patient Choice Ranker"
        >
          <ClipboardList size={18} />
          <span>Form Ranker</span>
        </button>

        <div className="tool-dock-divider" />

        <button
          className={`tool-dock-btn ${showChatPanel ? "tool-dock-active" : ""} ${!selectedHospital ? "tool-dock-disabled" : ""}`}
          onClick={() => selectedHospital && setShowChatPanel((v) => !v)}
          title={selectedHospital ? "AI Assistant" : "Select a hospital to use the assistant"}
        >
          <MessageSquare size={18} />
          <span>Assistant</span>
        </button>

        <div className="tool-dock-divider" />

        <button
          className="tool-dock-btn"
          onClick={() => setShowIntroModal(true)}
          title="How it works"
        >
          <Info size={18} />
          <span>About</span>
        </button>

        <div className="tool-dock-divider" />

        <button
          className="tool-dock-btn"
          onClick={() => setShowDemoModal(true)}
          title="Request a demo"
        >
          <Mail size={18} />
          <span>Demo</span>
        </button>
      </nav>

      {/* Patient Choice full-page panel */}
      {showPatientChoicePanel && (
        <>
          <div
            className="pc-backdrop"
            onClick={() => setShowPatientChoicePanel(false)}
          />
          <PatientChoicePanel
            onClose={() => setShowPatientChoicePanel(false)}
            onRequestDemo={() => { setShowPatientChoicePanel(false); setShowDemoModal(true); }}
          />
        </>
      )}

    <div className="page">
      <header className="hero">
        <div>
          <p className="eyebrow">PAC Intelligence Platform</p>
          <h1>Choose Better Post-Acute Partners. Reduce Readmissions.</h1>
          <p className="lede">
            Identify high-quality SNFs near your hospital, compare performance across {formatCompactNumber(DATA_FOOTPRINT.snfs)} facilities, and evaluate how patient choice materials influence post-acute outcomes.
          </p>
        </div>
        <div className="hero-badge">
          <span className="dot" />
          <div className="badge-text">
            <span className="badge-title">Live CMS data</span>
            <span className="badge-value">
              {formatCompactNumber(
                DATA_FOOTPRINT.qrpRows + DATA_FOOTPRINT.snfs + DATA_FOOTPRINT.hospitals
              )}{" "}
              data points
            </span>
          </div>
        </div>
      </header>

      <section className="panel search-panel">
        <div className="search-control">
          <label>Hospital</label>
          <div className="input-wrap">
            <Search className="icon" />
            <input
              placeholder="Search for a hospital..."
              value={hospitalQuery}
              onChange={(e) => {
                setHospitalQuery(e.target.value);
                setSelectedHospital(null);
              }}
            />
          </div>
          {hospitalSuggestions.length > 0 && (
            <div className="suggestions">
              {hospitalSuggestions.map((h) => (
                <button
                  key={`${h.hospital_name}-${h.city}`}
                  onClick={() => {
                    setSelectedHospital(h);
                    setHospitalQuery(h.hospital_name);
                    setHospitalSuggestions([]);
                  }}
                >
                  <div className="suggestion-title">{h.hospital_name}</div>
                  <div className="suggestion-sub">
                    {h.city ? `${h.city}, ` : ""}
                    {h.state}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="search-actions">
          <button className="ghost-btn" onClick={() => setHospitalQuery("")}>
            Clear
          </button>
          <button className="primary-btn" onClick={handleAnalyze} disabled={!selectedHospital || loading}>
            {loading ? (
              <>
                <RefreshCw className="icon spin" /> Running analysis...
              </>
            ) : (
              <>
                Run analysis <ArrowRight className="icon" />
              </>
            )}
          </button>
        </div>
        {selectedHospital && !loading && (
          <p className="selected-note">
            Selected: <strong>{selectedHospital.hospital_name}</strong>
            {selectedHospital.city && ` (${selectedHospital.city}, ${selectedHospital.state})`}
          </p>
        )}
        {searchError && <div className="alert">{searchError}</div>}
        {error && <div className="alert">{error}</div>}
      </section>

      <div className="layout">
        <div className="main">
          {facilities.length > 0 ? (
            <>
              <section className="stat-grid">
                <div className="stat-card">
                  <p className="label">Facilities in scope</p>
                  <p className="value">{facilities.length}</p>
                  <p className="hint">Within 50 miles of {lastAnalyzedHospital?.name}</p>
                </div>
                <div className="stat-card">
                  <p className="label">Average distance</p>
                  <p className="value">{summary ? `${summary.avgDistance.toFixed(1)} mi` : "—"}</p>
                  <p className="hint">door-to-door estimate</p>
                </div>
                <div className="stat-card">
                  <p className="label">Composite score</p>
                  <p className="value">{summary ? summary.avgComposite.toFixed(0) : "—"}</p>
                  <p className="hint">average across returned facilities</p>
                </div>
                <div className="stat-card">
                  <p className="label">Historical coverage</p>
                  <p className="value">{summary ? summary.historicalCoverage.toFixed(1) : "0.0"}</p>
                  <p className="hint">avg. years of data available</p>
                </div>
              </section>

              <section className="facility-list">
                {facilities.map((facility) => {
                  const badge = compositeBadge(facility.Composite_Score);
                  const regulatorySnapshot = buildRegulatorySnapshot(facility);
                  const facilityKey = facility.provider_id || facility.facility_name;
                  const isExpanded = Boolean(expandedFacilities[facilityKey]);
                  const toggleExpanded = () =>
                    setExpandedFacilities((prev) => ({
                      ...prev,
                      [facilityKey]: !prev[facilityKey],
                    }));

                  const compactMetrics = [
                    {
                      label: "Mobility at Discharge",
                      value: resolveMetricValue(
                        facility,
                        metricDefinitions.find((m) => m.historicalKey === "mobility_at_discharge")
                      ).value,
                      unit: "%",
                    },
                    {
                      label: "Discharge to Community",
                      value: resolveMetricValue(
                        facility,
                        metricDefinitions.find((m) => m.historicalKey === "discharge_to_community_rate")
                      ).value,
                      unit: "%",
                    },
                    {
                      label: "Infection Rate",
                      value: resolveMetricValue(
                        facility,
                        metricDefinitions.find((m) => m.historicalKey === "healthcare_associated_infection_rate")
                      ).value,
                      unit: "%",
                    },
                    {
                      label: "Pressure Ulcer Rate",
                      value: resolveMetricValue(
                        facility,
                        metricDefinitions.find((m) => m.historicalKey === "pressure_ulcer_rate")
                      ).value,
                      unit: "%",
                    },
                  ];

                  const review = facility.review_enrichment;
                  const reviewSnippets = review?.recent_reviews?.slice(0, 3) || [];

                  return (
                    <div key={facilityKey} className="facility-card">
                      <div className="facility-header">
                        <div>
                          <div className="meta">
                            <MapPin className="icon tiny" />
                            {formatNumber(facility.distance, 1)} miles from hospital
                          </div>
                          <h2>{facility.facility_name}</h2>
                          <p className="meta">
                            {facility.city && `${facility.city}, `}
                            {facility.state} · CCN {facility.CCN || facility.provider_id}
                          </p>
                        </div>
                        <div className="metrics-mini">
                          <div>
                            <p className="label">Composite</p>
                            <p className="value">{formatNumber(facility.Composite_Score, 0)}</p>
                            <span className={`pill ${badge.bg}`}>{badge.label}</span>
                          </div>
                          <div>
                            <p className="label">CMS Stars</p>
                            <p className="value">{facility.overall_rating ?? "—"}</p>
                          </div>
                          <div>
                            <p className="label">Hist. coverage</p>
                            <p className="value">{facility.historical_years_available || 0}</p>
                            <p className="hint">years</p>
                          </div>
                        </div>
                      </div>

                      <div className="compact-metrics">
                        {compactMetrics.map((m) => (
                          <div key={m.label} className="compact-metric">
                            <p className="compact-label">{m.label}</p>
                            <p className="compact-value">
                              {formatNumber(m.value)}
                              {m.value !== null && m.value !== undefined && m.value !== "—" && m.unit ? (
                                <span className="unit">{m.unit}</span>
                              ) : null}
                            </p>
                          </div>
                        ))}
                        <button className="toggle-btn" onClick={toggleExpanded}>
                          {isExpanded ? (
                            <>
                              Hide details <ChevronUp className="icon tiny" />
                            </>
                          ) : (
                            <>
                              Show details <ChevronDown className="icon tiny" />
                            </>
                          )}
                        </button>
                      </div>

                      {isExpanded && (
                        <>
                          <div className="metric-groups">
                            <div className="metrics-toolbar">
                              <button
                                type="button"
                                className="ghost-btn"
                                onClick={() => setShowAllMeasures((prev) => !prev)}
                              >
                                {showAllMeasures ? "Show fewer measures" : "Show all measures"}
                              </button>
                              <span className="hint">Primary measures are shown by default.</span>
                            </div>
                            {Object.entries(metricGroupLabels).map(([groupId, title]) => {
                              if (!showAllMeasures && !PRIMARY_GROUPS.has(groupId)) return null;
                              const metrics = metricDefinitions.filter((m) => m.group === groupId);
                              if (!metrics.length) return null;
                              return (
                                <div key={groupId} className="metric-group">
                                  <div className="metric-group-head">
                                    <h3>{title}</h3>
                                    <span>Current + History</span>
                                  </div>
                                  <div className="metric-grid">
                                    {metrics.map((metric) => {
                                      const resolved = resolveMetricValue(facility, metric);
                                      return (
                                        <div key={metric.label} className="metric-card">
                                          <div className="metric-top">
                                            <span>{metric.label}</span>
                                            <span className="source-tag">
                                              {resolved.source === "current"
                                                ? "Current (CMS)"
                                                : resolved.source === "snapshot"
                                                ? "Snapshot (CMS)"
                                                : resolved.source === "historical"
                                                ? "Historical"
                                                : "No data"}
                                            </span>
                                          </div>
                                          <div className="metric-body">
                                            <div>
                                              <div className="metric-value">
                                                {formatNumber(resolved.value)}
                                                {metric.unit && resolved.value != null && <span className="unit">{metric.unit}</span>}
                                              </div>
                                              <p className="metric-desc">{metric.description}</p>
                                              <p className="metric-pref">{metric.preference}</p>
                                            </div>
                                            {resolved.value != null ? (
                                              resolved.trend ? (
                                                <div className="trend-wrap">
                                                  <div className={`trend-chip ${resolved.trend.good ? "good" : "bad"}`}>
                                                    {resolved.trend.delta >= 0 ? (
                                                      <TrendingUp className="icon tiny" />
                                                    ) : (
                                                      <TrendingDown className="icon tiny" />
                                                    )}
                                                    <span className="trend-delta">
                                                      {resolved.trend.delta > 0 ? "+" : ""}
                                                      {resolved.trend.delta.toFixed(1)}
                                                      {metric.unit}
                                                    </span>
                                                    <span className="trend-years">{resolved.trend.yearSpan}</span>
                                                  </div>
                                                  <div className="trend-timeline">
                                                    <div className="trend-node">
                                                      <span className="trend-label">Start</span>
                                                    <span className="trend-value">
                                                        {formatNumber(resolved.trend.startValue)}
                                                        {metric.unit}
                                                      </span>
                                                    </div>
                                                    <span className="trend-line"></span>
                                                    <div className="trend-node">
                                                      <span className="trend-label">Latest</span>
                                                      <span className="trend-value">
                                                        {formatNumber(resolved.trend.endValue)}
                                                        {metric.unit}
                                                      </span>
                                                    </div>
                                                  </div>
                                                </div>
                                              ) : (
                                                <div className="hint">Most recent: {resolved.mostRecentYear || "—"}</div>
                                              )
                                            ) : (
                                              <div className="hint">N/A</div>
                                            )}
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              );
                            })}
                          </div>

                          <div className="reg-block">
                            <div className="reg-head">
                              <h3>Regulatory overview</h3>
                              {regulatorySnapshot.citationYear && (
                                <span className="hint">
                                  Latest citation year: {regulatorySnapshot.citationYear}
                                  {regulatorySnapshot.latestSurveyDate ? ` · Survey: ${regulatorySnapshot.latestSurveyDate}` : ""}
                                </span>
                              )}
                            </div>
                            {regulatorySnapshot.citationData || regulatorySnapshot.penaltyData ? (
                              <div className="reg-grid">
                                <div className="reg-card">
                                  <p className="label">Citations (latest year)</p>
                                  {regulatorySnapshot.citationData ? (
                                    <div className="reg-rows">
                                      <div className="row">
                                        <span>Total citations</span>
                                        <span className="value-sm">{regulatorySnapshot.citationData.total}</span>
                                      </div>
                                      <div className="row">
                                        <span>Complaint citations</span>
                                        <span>{regulatorySnapshot.citationData.complaintDeficiencies ?? "—"}</span>
                                      </div>
                                      <div className="row">
                                        <span>Standard citations</span>
                                        <span>{regulatorySnapshot.citationData.standardDeficiencies ?? "—"}</span>
                                      </div>
                                      <div className="row">
                                        <span>Immediate jeopardy</span>
                                        <span>{regulatorySnapshot.citationData.immediateJeopardy}</span>
                                      </div>
                                    </div>
                                  ) : (
                                    <p className="hint">No recent citations reported.</p>
                                  )}
                                </div>
                                <div className="reg-card">
                                  <p className="label">Penalties (latest year)</p>
                                  {regulatorySnapshot.penaltyData ? (
                                    <div className="reg-rows">
                                      <div className="row">
                                        <span>Total penalties</span>
                                        <span className="value-sm">{regulatorySnapshot.penaltyData.penalties}</span>
                                      </div>
                                      <div className="row">
                                        <span>Fines issued</span>
                                        <span>{regulatorySnapshot.penaltyData.finesCount}</span>
                                      </div>
                                      <div className="row">
                                        <span>Cost of fines</span>
                                        <span>{formatCurrency(regulatorySnapshot.penaltyData.finesTotal || 0)}</span>
                                      </div>
                                      <div className="row">
                                        <span>Payment denials</span>
                                        <span>{regulatorySnapshot.penaltyData.paymentDenials}</span>
                                      </div>
                                    </div>
                                  ) : (
                                    <p className="hint">No monetary penalties recorded.</p>
                                  )}
                                </div>
                              </div>
                            ) : (
                              <p className="hint">No regulatory data available yet.</p>
                            )}

                            {(regulatorySnapshot.topCategories.length ||
                              regulatorySnapshot.topTags.length ||
                              regulatorySnapshot.recentCitations.length) && (
                              <div className="reg-detail">
                                <div className="reg-card">
                                  <p className="label">Why they got cited</p>
                                  {regulatorySnapshot.topCategories.length ? (
                                    <div className="reg-tags">
                                      {regulatorySnapshot.topCategories.slice(0, 4).map((item) => (
                                        <span key={item.category} className="reg-tag-pill">
                                          {item.category} · {item.count}
                                        </span>
                                      ))}
                                    </div>
                                  ) : (
                                    <p className="hint">No category breakdown available.</p>
                                  )}
                                  {regulatorySnapshot.topTags.length ? (
                                    <div className="reg-tags secondary">
                                      {regulatorySnapshot.topTags.slice(0, 4).map((item) => (
                                        <span key={item.tag} className="reg-tag-pill alt">
                                          {item.tag} · {item.count}
                                        </span>
                                      ))}
                                    </div>
                                  ) : null}
                                </div>
                                <div className="reg-card">
                                  <p className="label">Recent citation details</p>
                                  {regulatorySnapshot.recentCitations.length ? (
                                    <div className="reg-recent">
                                      {regulatorySnapshot.recentCitations.slice(0, 4).map((item, idx) => (
                                        <div key={`${item.tag || "tag"}-${idx}`} className="reg-recent-item">
                                          <div className="reg-recent-head">
                                            <span className="reg-tag">{item.tag || "Citation tag n/a"}</span>
                                            <span className="reg-meta">
                                              {item.surveyDate || "Date n/a"}
                                              {item.scopeSeverity ? ` · Severity ${item.scopeSeverity}` : ""}
                                            </span>
                                          </div>
                                          {item.description && <p className="reg-desc">{item.description}</p>}
                                          {item.category && <p className="hint">{item.category}</p>}
                                        </div>
                                      ))}
                                    </div>
                                  ) : (
                                    <p className="hint">No recent citation details available.</p>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>

                          <div className="review-block">
                            <div className="review-head">
                              <div>
                                <h3>Community voice</h3>
                                <p className="hint">Recent Google feedback</p>
                              </div>
                              <div className="review-stats">
                                {review?.google_rating != null && (
                                  <div className="review-pill">
                                    <span className="pill-label">Google rating</span>
                                    <span className="pill-value">{formatNumber(review.google_rating, 1)}</span>
                                    {review?.google_rating_count ? <span className="pill-sub">({review.google_rating_count} reviews)</span> : null}
                                  </div>
                                )}
                                {review?.recent_avg_rating != null && (
                                  <div className="review-pill alt">
                                    <span className="pill-label">Recent avg</span>
                                    <span className="pill-value">{formatNumber(review.recent_avg_rating, 1)}</span>
                                    {review?.recent_review_count ? <span className="pill-sub">last {review.recent_review_count} reviews</span> : null}
                                  </div>
                                )}
                              </div>
                            </div>
                            {reviewSnippets.length ? (
                              <div className="review-grid">
                                {reviewSnippets.map((quote, idx) => (
                                  <div key={idx} className="review-quote">
                                    <p className="quote-text">"{truncateReview(quote)}"</p>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <p className="hint">No recent Google review snippets yet.</p>
                            )}
                          </div>
                        </>
                      )}

                      {facility.historical_years_available === 0 && <p className="hint">Historical CMS data not available for this CCN yet.</p>}
                    </div>
                  );
                })}
              </section>
            </>
          ) : lastAnalyzedHospital ? (
            <div className="empty">
              <Activity className="icon" />
              No facilities found within the default radius. Try a different hospital or widen your radius in the backend request.
            </div>
          ) : (
            <div className="empty">
              <Activity className="icon" />
              Choose a hospital to begin exploring nearby SNFs.
            </div>
          )}
        </div>
      </div>

      {showChatPanel && (
        <div className="floating-chat-panel">
          <div className="floating-chat-header">
            <div>
              <p className="label">AI Assistant</p>
              <p className="hint">
                {selectedHospital
                  ? `Context: ${selectedHospital.hospital_name}`
                  : "Select a hospital to ask about nearby SNFs."}
              </p>
            </div>
            <button className="ghost-btn close" onClick={() => setShowChatPanel(false)}>
              <X className="icon tiny" /> Close
            </button>
          </div>
          <div className="chat-body">
            {chatHistory.length === 0 && (
              <p className="hint">Ask about high-performing SNFs, trends, or risks near the selected hospital.</p>
            )}
            {chatHistory.map((msg, idx) => (
              <div key={idx} className={`chat-bubble ${msg.role === "user" ? "user" : "assistant"}`}>
                <span dangerouslySetInnerHTML={formatMessageHtml(msg.text)} />
              </div>
            ))}
          </div>
          {chatError && <div className="alert">{chatError}</div>}
          <div className="chat-input">
            <textarea
              rows={2}
              placeholder={
                selectedHospital ? `Ask about facilities near ${selectedHospital.hospital_name}...` : "Select a hospital first..."
              }
              value={chatInput}
              onChange={(e) => setChatInput(e.target.value)}
              disabled={!selectedHospital || chatLoading}
            />
            <button className="primary-btn" onClick={handleChatSubmit} disabled={!selectedHospital || chatLoading || !chatInput.trim()}>
              {chatLoading ? "Working..." : "Send"}
            </button>
          </div>
        </div>
      )}

      <footer className="page-footer-cta">
        <p>Post-acute performance matters more than ever. See how your hospital's SNF network compares.</p>
        <button className="primary-btn" onClick={() => setShowDemoModal(true)}>
          Request Demo <ArrowRight className="icon" />
        </button>
      </footer>
    </div>

    {showIntroModal && (
      <IntroModal
        onClose={() => setShowIntroModal(false)}
        onRequestDemo={() => { setShowIntroModal(false); setShowDemoModal(true); }}
      />
    )}
    {showDemoModal && <DemoModal onClose={() => setShowDemoModal(false)} />}
    </div>
  );
};

export default SNFDashboard;
